import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, addDays } from "date-fns";
import { mdyDowTime } from "@/lib/dateFmt";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Crown, Zap, Clock, Users2, BadgeCheck, Flame, CheckCircle2, Ban, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// 6-row Sun-anchored month grid
function monthGrid(monthIso) {
  const [y, m] = monthIso.slice(3).split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startDow = first.getDay();
  const gridStart = addDays(first, -startDow);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/**
 * Manager overview of the NSO board for the current facility.
 * Same monthly calendar as the employee view, but each day-cell surfaces
 * every open slot with the ordered list of requesters (tiles) and highlights
 * the projected auto-assign winner (highest-seniority non-OT) so the manager
 * can see who will get the shift when the window closes.
 */
export default function NsoManagerView({ facilityId }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/nso/manager/state?facility_id=${facilityId}`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load NSO overview");
    } finally {
      setLoading(false);
    }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  const runAutoAssign = useCallback(async () => {
    if (!state?.facility_id || !state?.target_month_id) return;
    const nOpen = (state.open_slots || []).length;
    const nReqs = (state.requests || []).filter(r => r.status === "pending" || r.status === "ot_review").length;
    const msg = state.is_open
      ? `The request window is still open. Running now will pick winners with only the ${nReqs} request(s) submitted so far. Continue?`
      : `Assign ${nOpen} open slot(s) to their highest-seniority non-OT requester now?`;
    if (!window.confirm(msg)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/nso/run-auto-assign", {
        facility_id: state.facility_id,
        target_month_id: state.target_month_id,
      });
      toast.success("Auto-Assign complete", {
        description: `${data.assigned || 0} assigned · ${data.bumped || 0} bumped · ${data.ot_review || 0} OT review`,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-Assign failed");
    } finally {
      setRunning(false);
    }
  }, [state, load]);

  const targetMonthLabel = useMemo(() => {
    if (!state?.target_month_id) return "";
    try {
      const [y, m] = state.target_month_id.slice(3).split("-").map(Number);
      return format(new Date(y, m - 1, 1), "MMMM yyyy");
    } catch { return state.target_month_id; }
  }, [state?.target_month_id]);

  const closesAtLabel = useMemo(() => {
    if (!state?.closes_at) return "";
    try { return mdyDowTime(state.closes_at); }
    catch { return state.closes_at; }
  }, [state?.closes_at]);

  if (loading || !state) {
    return <div className="p-6 text-slate-500" data-testid="nso-mgr-loading">Loading NSO overview…</div>;
  }
  if (!state.facility_id || state.detail) {
    return (
      <div className="p-6" data-testid="nso-mgr-empty">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
          <div className="text-lg font-semibold text-slate-800">Not available</div>
          <div className="text-sm text-slate-500 mt-1">
            NSO is only enabled for Auburn / Solano. Switch facilities to view its overview.
          </div>
        </div>
      </div>
    );
  }

  const days = monthGrid(state.target_month_id);
  const codeMap = new Map((state.shift_codes || []).map(c => [c.code, c]));
  const empMap = new Map((state.participants || []).map((p, i) => [p.id, { ...p, rank: i + 1 }]));
  // Bucket slot_details by date
  const slotsByDate = new Map();
  (state.slot_details || []).forEach(s => {
    const arr = slotsByDate.get(s.date) || [];
    arr.push(s);
    slotsByDate.set(s.date, arr);
  });

  // KPIs
  const totalOpen = (state.open_slots || []).length;
  const totalReqs = (state.requests || []).length;
  const projAssigned = (state.slot_details || []).filter(s => s.projected_winner_id).length;
  const otReview = (state.requests || []).filter(r => r.wants_ot || r.status === "ot_review").length;

  return (
    <TooltipProvider delayDuration={100}>
      <div className="p-3 md:p-6 space-y-4" data-testid="nso-mgr-page">
        {/* Header banner */}
        <div className={`rounded-2xl border-2 p-4 flex flex-col md:flex-row md:items-center gap-3 ${state.is_open ? "border-emerald-400 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}>
          <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 shadow ${state.is_open ? "bg-emerald-500 text-white" : "bg-slate-400 text-white"}`}>
            {state.is_open ? <Zap className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black uppercase tracking-widest text-sm text-slate-800">
              {state.is_open
                ? `Manager overview · window closes ${closesAtLabel}`
                : "Manager overview · window closed"}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Requesting <b>{targetMonthLabel}</b> open shifts. Winners are highlighted; hover any tile for shift details and seniority.
            </div>
          </div>
          <Button
            type="button"
            onClick={runAutoAssign}
            disabled={running}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white shrink-0"
            data-testid="btn-nso-run-autoassign"
            title="Auto-assign every open slot to the highest-seniority non-OT requester now"
          >
            {running
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Running…</>
              : <><Sparkles className="w-4 h-4 mr-1.5" /> Run Auto-Assign now</>}
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Open slots"       value={totalOpen}       tone="amber" />
          <Kpi label="Total requests"   value={totalReqs}       tone="indigo" />
          <Kpi label="Projected wins"   value={projAssigned}    tone="emerald" />
          <Kpi label="OT-review"        value={otReview}        tone="orange" />
        </div>

        {/* Participants roster */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2 mb-2">
            <Users2 className="w-4 h-4 text-slate-500" />
            <div className="font-semibold text-slate-800 text-sm">Seniority roster</div>
            <div className="text-[11px] text-slate-500 ml-1">({(state.participants || []).length})</div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(state.participants || []).map((p, i) => (
              <Tooltip key={p.id}>
                <TooltipTrigger asChild>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 pl-1 pr-2 py-0.5 text-xs">
                    <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold grid place-items-center">
                      {i + 1}
                    </span>
                    <span className="text-slate-800">{p.name}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-[11px] text-slate-100 mt-0.5">
                    Rank #{i + 1}{p.hire_date ? ` · hired ${p.hire_date}` : ""}
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
            {(state.participants || []).length === 0 && (
              <span className="text-xs text-slate-500 italic">No participants enrolled yet.</span>
            )}
          </div>
        </div>

        {/* Month grid */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="font-semibold text-slate-800 text-sm">{targetMonthLabel}</div>
            <div className="text-[11px] text-slate-500">
              Hover any tile for details · <span className="inline-flex items-center gap-1"><Crown className="w-3 h-3 text-amber-500" /> projected winner</span>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-slate-400 px-1 mb-1">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const iso = format(d, "yyyy-MM-dd");
              const inMonth = iso.startsWith(state.target_month_id.slice(3));
              const slots = slotsByDate.get(iso) || [];
              return (
                <div key={iso}
                     className={`rounded-lg border p-1.5 min-h-[120px] text-[11px] ${inMonth ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-50"}`}
                     data-testid={`nso-mgr-day-${iso}`}>
                  <div className="text-slate-400 font-semibold">{format(d, "d")}</div>
                  <div className="space-y-1 mt-1">
                    {slots.map((s, si) => {
                      const codeMeta = codeMap.get(s.shift_code);
                      const dept = codeMeta?.department;
                      return (
                        <div key={si} className="rounded border border-slate-200 bg-slate-50/70 p-1"
                             data-testid={`nso-mgr-slot-${iso}-${s.shift_code}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-700 cursor-help">
                                <span className="inline-flex items-center rounded px-1 py-px bg-amber-100 text-amber-800 border border-amber-300">
                                  {s.shift_code}
                                </span>
                                {dept && (
                                  <span className="inline-flex items-center gap-0.5 text-[9px] text-slate-500">
                                    <BadgeCheck className="w-2.5 h-2.5" />
                                    {dept}
                                  </span>
                                )}
                                {s.requesters.length === 0 && (
                                  <span className="text-[9px] text-slate-400 italic">no requests</span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[280px]">
                              <div className="font-semibold">{codeMeta?.label || s.shift_code}</div>
                              <div className="text-[11px] text-slate-100 mt-0.5">
                                {codeMeta?.start && codeMeta?.end ? `${codeMeta.start} – ${codeMeta.end}` : "hours vary"}
                                {codeMeta?.hours ? ` · ${codeMeta.hours}h` : ""}
                              </div>
                              {dept && (
                                <div className="text-[11px] text-slate-100 mt-0.5 flex items-center gap-1">
                                  <BadgeCheck className="w-3 h-3" /> {dept}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>

                          {/* Requester tiles */}
                          {s.requesters.map(r => {
                            const meta = empMap.get(r.employee_id);
                            const isWinner = s.projected_winner_id === r.employee_id;
                            const assigned = r.status === "assigned";
                            const bumped = r.status === "bumped";
                            const otReview = r.wants_ot || r.status === "ot_review";
                            return (
                              <Tooltip key={r.id}>
                                <TooltipTrigger asChild>
                                  <div
                                    className={`mt-1 flex items-center gap-1 rounded px-1 py-0.5 border text-[10px] cursor-help ${
                                      assigned ? "bg-emerald-50 border-emerald-400 text-emerald-800"
                                      : isWinner ? "bg-amber-50 border-amber-400 text-amber-900 font-semibold"
                                      : otReview ? "bg-orange-50 border-orange-300 text-orange-800"
                                      : bumped   ? "bg-slate-100 border-slate-300 text-slate-500 line-through"
                                      : "bg-white border-slate-200 text-slate-700"
                                    }`}
                                    data-testid={`nso-mgr-req-${r.id}`}
                                  >
                                    {isWinner && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
                                    {assigned && <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />}
                                    {otReview && <Flame className="w-3 h-3 text-orange-500 shrink-0" />}
                                    <span className="w-4 h-4 rounded-full bg-slate-900 text-white text-[8px] font-bold grid place-items-center shrink-0">
                                      {(meta?.rank ?? "?")}
                                    </span>
                                    <span className="truncate">
                                      {(r.employee_name || meta?.name || "—").split(" ")[0]}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs max-w-[280px]">
                                  <div className="font-semibold">{r.employee_name}</div>
                                  <div className="text-[11px] text-slate-100 mt-0.5">
                                    Seniority rank <b>#{meta?.rank ?? "—"}</b>
                                    {meta?.hire_date ? ` · hired ${meta.hire_date}` : ""}
                                  </div>
                                  <div className="text-[11px] text-slate-100 mt-1">
                                    Status: <b className="uppercase">{r.status}</b>
                                    {r.wants_ot ? " · wants OT" : ""}
                                  </div>
                                  {r.rejection_reason && (
                                    <div className="text-[11px] text-red-200 mt-1">
                                      {r.rejection_reason}
                                    </div>
                                  )}
                                  {isWinner && (
                                    <div className="text-[11px] text-amber-200 mt-1 flex items-center gap-1">
                                      <Crown className="w-3 h-3" /> Projected winner at close
                                    </div>
                                  )}
                                  {(r.ot_warnings?.length || 0) > 0 && (
                                    <div className="text-[11px] text-orange-200 mt-1">
                                      OT: {r.ot_warnings.join(" · ")}
                                    </div>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

function Kpi({ label, value, tone }) {
  const map = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    orange: "bg-orange-50 border-orange-200 text-orange-900",
  };
  return (
    <div className={`rounded-xl border p-3 ${map[tone] || ""}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black mt-0.5">{value}</div>
    </div>
  );
}
